import { Paper, Typography } from "@mui/material";
import DrWeedyLogo from "../assets/DrWeedyLogo";

/**
 * O que é o quadro de segurança que aparece no fim das respostas do chat.
 *
 * Existe porque o bloco se repetia a cada turno carregando a definição de cada
 * rótulo, as fontes e a regra de precedência — ~25 linhas de glossário por
 * resposta (decisão de 17/09/2026). A parte GENÉRICA mora aqui; no chat ficam
 * só o veredito por formulação e o motivo de cada bloqueio, que é o que o D6 do
 * dossiê exige que o médico leia.
 *
 * ⚠️ Os rótulos abaixo são a `classificacao_desfechos` da matriz de segurança
 * (`protocols.json` do agent-cannabis). Ao mudar lá, mudar aqui: a versão da
 * matriz que gerou cada quadro sai impressa no rodapé do bloco, no chat.
 */

const ROTULOS = [
  {
    cor: "🟢",
    nome: "PODE SER CONSIDERADO",
    texto:
      "Não foi identificado bloqueio específico para aquela molécula ou formulação.",
  },
  {
    cor: "🟡",
    nome: "REVISÃO MÉDICA OBRIGATÓRIA",
    texto:
      "O tratamento pode ser possível, mas não deve ser recomendado automaticamente pelo agente.",
  },
  {
    cor: "🔵",
    nome: "USO EXCEPCIONAL",
    texto:
      "Não recomendado rotineiramente. Somente considerar diante de indicação clínica específica, justificativa clínica e avaliação médica individualizada.",
  },
  {
    cor: "⚪",
    nome: "NÃO RECOMENDADO (evidência insuficiente)",
    texto:
      "Não necessariamente contraindicado, mas não deve constituir escolha automática.",
  },
  {
    cor: "🟠",
    nome: "NÃO INICIAR AGORA",
    texto:
      "Existe condição aguda que precisa ser estabilizada, investigada ou tratada prioritariamente.",
  },
  {
    cor: "🔴",
    nome: "NÃO PRESCREVER",
    texto:
      "Risco superior ao benefício provável ou contraindicação definida para aquela molécula/formulação.",
  },
];

const FONTES = [
  {
    nome: "Matriz clínica geral",
    texto:
      "Situações clínicas que contraindicam ou restringem uma formulação — por exemplo gestação, quadro psicótico ou hepatopatia.",
  },
  {
    nome: "Matriz por faixa etária",
    texto:
      "O que cada faixa de idade admite. Sem a idade do paciente, esta fonte não se aplica e a linha sai como não avaliada.",
  },
  {
    nome: "Algoritmo de seleção (iCnP)",
    texto:
      "As regras do protocolo de seleção, incluindo as que dependem de contexto e não de diagnóstico — ocupação que exige ausência total de THC, atleta sujeito a doping, intolerância prévia.",
  },
];

const Secao = ({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) => (
  <section className="mb-8">
    <Typography
      variant="h6"
      sx={{
        color: "#ffffff",
        fontWeight: 700,
        fontSize: "17px",
        marginBottom: "12px",
        fontFamily: "Oxanium, sans-serif",
      }}
    >
      {titulo}
    </Typography>
    {children}
  </section>
);

const Paragrafo = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      color: "rgba(255,255,255,0.75)",
      fontSize: "15px",
      lineHeight: 1.7,
      marginBottom: "12px",
    }}
  >
    {children}
  </Typography>
);

export default function SegurancaPage() {
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>

      <div className="w-full flex flex-col relative z-10">
        <div className="flex items-center gap-3 px-8 py-4">
          <DrWeedyLogo width={40} height={40} />
          <Typography
            variant="h6"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontFamily: "Oxanium, sans-serif",
            }}
          >
            Dr. Weedy
          </Typography>
        </div>

        <div className="flex-1 flex justify-center px-6 py-4">
          <div className="max-w-3xl w-full">
            <Typography
              variant="h4"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                marginBottom: "8px",
                letterSpacing: "-0.03em",
                fontFamily: "Oxanium, sans-serif",
              }}
            >
              🛡️ A triagem de segurança
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "15px",
                marginBottom: "24px",
              }}
            >
              O que é o quadro que aparece no fim das respostas
            </Typography>

            <Paper
              elevation={8}
              sx={{
                background:
                  "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
                backdropFilter: "blur(20px)",
                border: "2px solid rgba(226, 114, 91, 0.2)",
                borderRadius: "24px",
                padding: "32px",
                marginBottom: "40px",
              }}
            >
              <Secao titulo="Quem escreve o quadro">
                <Paragrafo>
                  O quadro não é escrito pelo modelo de linguagem. Ele é montado
                  por regra determinística, a partir das matrizes de segurança
                  do protocolo, e anexado à resposta pelo código — mesmo que o
                  texto acima dele diga outra coisa. É por isso que ele aparece
                  sempre, e sempre igual para o mesmo caso.
                </Paragrafo>
                <Paragrafo>
                  Ele avalia cada formulação separadamente. Não escolhe conduta:
                  a escolha é sua.
                </Paragrafo>
              </Secao>

              <Secao titulo="O que cada rótulo quer dizer">
                {ROTULOS.map((r) => (
                  <Paragrafo key={r.nome}>
                    <strong style={{ color: "#ffffff" }}>
                      {r.cor} {r.nome}
                    </strong>
                    {" — "}
                    {r.texto}
                  </Paragrafo>
                ))}
                <Paragrafo>
                  <strong style={{ color: "#ffffff" }}>
                    🟢 não é aval, recomendação nem aprovação.
                  </strong>{" "}
                  Quer dizer apenas que nenhuma das fontes identificou bloqueio
                  para aquela formulação neste caso — o que permite que ela
                  avance para a próxima etapa da decisão, que é a sua.
                </Paragrafo>
              </Secao>

              <Secao titulo="De onde vem cada veredito">
                <Paragrafo>
                  Três fontes são consultadas para cada formulação:
                </Paragrafo>
                {FONTES.map((f) => (
                  <Paragrafo key={f.nome}>
                    <strong style={{ color: "#ffffff" }}>{f.nome}</strong>
                    {" — "}
                    {f.texto}
                  </Paragrafo>
                ))}
              </Secao>

              <Secao titulo="Quando as fontes discordam">
                <Paragrafo>
                  Prevalece sempre a mais restritiva. Se a matriz etária admite
                  uma formulação e o algoritmo de seleção a veta, o veredito é o
                  do algoritmo. Nessas situações o quadro diz, na resposta, qual
                  fonte prevaleceu e por quê — é a informação que muda a
                  conduta, e por isso ela continua na tela em vez de ficar aqui.
                </Paragrafo>
              </Secao>

              <Secao titulo="“Não avaliado” não é “sem contraindicação”">
                <Paragrafo>
                  Parte das situações da matriz clínica depende de dado que o
                  prontuário não traz — função hepática, risco suicida,
                  capacidade de monitoramento, entre outros. Essas situações não
                  são avaliadas, e o quadro diz quantas ficaram de fora. Uma
                  formulação sem bloqueio pode ter deixado de ser testada contra
                  a condição que importa para o seu paciente.
                </Paragrafo>
                <Paragrafo>
                  O mesmo vale para a idade: sem ela, a matriz por faixa etária
                  não se aplica e a linha inteira sai como não avaliada.
                </Paragrafo>
              </Secao>

              <Secao titulo="Versão da matriz">
                <Paragrafo>
                  Cada quadro traz, no rodapé, a versão da matriz de segurança
                  que o gerou. Ela serve para rastrear, depois, sob qual versão
                  do dado uma decisão foi tomada.
                </Paragrafo>
              </Secao>
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
}
